/**
 * Fallback expiry when neither JWT `exp` nor `expires_in` is available.
 * Server currently issues 30-minute tokens (expires_in: 1800).
 */
export const ACCESS_TOKEN_FALLBACK_TTL_MS = 30 * 60 * 1000;

/** Schedule proactive refresh this many ms before the token actually expires (2 minutes). */
export const REFRESH_BEFORE_EXPIRY_MS = 2 * 60 * 1000;

/** Log the user out after this many ms of inactivity (15 min). */
export const IDLE_LOGOUT_MS = 15 * 60 * 1000;

const EXPIRES_AT_STORAGE_KEY = "access_token_expires_at";

/** Coerce API `expires_in` (number or numeric string) to seconds, or null. */
export function parseExpiresInSeconds(expiresInSeconds) {
  const n = Number(expiresInSeconds);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function parseJwtExpiryMs(accessToken) {
  if (!accessToken || typeof accessToken !== "string") return null;
  const parts = accessToken.split(".");
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "="
    );
    const payload = JSON.parse(atob(padded));
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Persist when the current access token expires.
 * Priority: expiresInSeconds (from server response) → JWT exp → fallback TTL.
 *
 * @param {string} accessToken
 * @param {number} [expiresInSeconds]  The `expires_in` value from the login/refresh response.
 */
export function persistAccessTokenExpiry(accessToken, expiresInSeconds) {
  let expiresAt;
  const seconds = parseExpiresInSeconds(expiresInSeconds);
  if (seconds) {
    expiresAt = Date.now() + seconds * 1000;
  } else {
    const jwtExp = parseJwtExpiryMs(accessToken);
    expiresAt = jwtExp ?? Date.now() + ACCESS_TOKEN_FALLBACK_TTL_MS;
  }
  localStorage.setItem(EXPIRES_AT_STORAGE_KEY, String(expiresAt));
  return expiresAt;
}

export function readStoredAccessTokenExpiryMs() {
  const raw = localStorage.getItem(EXPIRES_AT_STORAGE_KEY);
  if (!raw) return null;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) ? n : null;
}

/**
 * When the access token expires (ms since epoch).
 * Prefer `access_token_expires_at` from login/refresh (`expires_in`), then JWT `exp`.
 */
export function readAccessTokenExpiryMs(accessToken) {
  const stored = readStoredAccessTokenExpiryMs();
  if (stored) return stored;
  return parseJwtExpiryMs(accessToken);
}

export function clearAccessTokenExpiryStorage() {
  localStorage.removeItem(EXPIRES_AT_STORAGE_KEY);
}
